import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { isGuest } from '../lib/guestMode';
import { useProfile } from '../context/ProfileContext';
import { useTheme } from '../context/ThemeContext';
import { supabase } from '../lib/supabase';

// Haversine formula to calculate distance between two points (in miles)
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  if (!lat1 || !lon1 || !lat2 || !lon2) return null;
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Math.round(R * c);
};

// Helper to format distance display
const formatDistance = (distance) => {
  if (distance === null || distance === undefined) return null;
  if (distance < 5) return '<5 mi';
  if (distance < 10) return '<10 mi';
  return `${distance} mi`;
};

// Icons
const MapPinIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const StarIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

const UsersIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const PlusIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </svg>
);

const CalendarIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const HeartIcon = ({ filled }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

const UserIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
    <circle cx="12" cy="7" r="4"></circle>
  </svg>
);

const EditIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6L6 18" />
    <path d="M6 6l12 12" />
  </svg>
);

// Location Card Component
const LocationCard = ({ location, rating, followerData, currentUserId, onToggleFollow, tier = 0, distance, onClick }) => {
  const { theme } = useTheme();

  // Free tier (0) = not clickable, just listed
  const isClickable = tier >= 1;

  // Tier styling: 0=Free (muted), 1=Basic, 2=Enhanced (verified), 3=Premium (gold)
  const getTierStyle = () => {
    switch (tier) {
      case 1: // Basic - standard clickable
        return { border: `1px solid ${theme.accent}`, cursor: 'pointer' };
      case 2: // Enhanced - verified green
        return { border: `2px solid #22c55e`, cursor: 'pointer' };
      case 3: // Premium - gold with glow
        return {
          border: `2px solid #f59e0b`,
          boxShadow: `0 0 20px rgba(245, 158, 11, 0.3)`,
          cursor: 'pointer',
        };
      default: // Free - muted, not clickable
        return { border: `1px solid ${theme.border}`, opacity: 0.7 };
    }
  };

  const handleClick = () => {
    if (isClickable && onClick) {
      onClick(location);
    }
  };

  // Has photo if tier >= 1 and photo_url exists
  const hasPhoto = tier >= 1 && location.photo_url;

  return (
    <div
      style={{ ...styles.locationCard, backgroundColor: theme.bgSheet, ...getTierStyle(), overflow: 'hidden' }}
      onClick={handleClick}
    >
      {/* Photo Banner for Basic+ */}
      {hasPhoto && (
        <div style={styles.cardImageContainer}>
          <img src={location.photo_url} alt={location.store_name} style={styles.cardImage} />
          {tier === 3 && (
            <div style={styles.premiumBanner}>
              <span style={styles.premiumBannerText}>PREMIUM PARTNER</span>
            </div>
          )}
        </div>
      )}
      <div style={styles.locationHeader}>
        <div style={styles.locationInfo}>
          <div style={styles.nameRow}>
            <span style={{ ...styles.locationName, color: theme.text }}>{location.store_name}</span>
            {/* Tier 2 = Verified badge (green), Tier 3 = Premium badge (gold) */}
            {tier >= 2 && (
              <div style={{
                ...styles.verifiedBadge,
                backgroundColor: tier === 3 ? 'rgba(245, 158, 11, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                borderColor: tier === 3 ? 'rgba(245, 158, 11, 0.3)' : 'rgba(16, 185, 129, 0.3)',
                color: tier === 3 ? '#f59e0b' : '#10b981',
              }}>
                <CheckCircleIcon />
                <span style={styles.verifiedText}>{tier === 3 ? 'Premium' : 'Verified'}</span>
              </div>
            )}
            {/* Free tier indicator */}
            {tier === 0 && (
              <span style={{ fontSize: '11px', color: theme.textMuted, fontStyle: 'italic' }}>Listed</span>
            )}
          </div>
          <div style={{ ...styles.addressRow, color: theme.textSecondary }}>
            <MapPinIcon />
            <span style={styles.locationAddress}>
              {location.city}, {location.state} {location.zip_code}
            </span>
            {distance !== null && distance !== undefined && (
              <span style={{ ...styles.distanceBadge, backgroundColor: `${theme.accent}26`, color: theme.accent }}>{formatDistance(distance)}</span>
            )}
          </div>
          {tier >= 2 && (
            <div style={{ ...styles.tradingBadge, backgroundColor: theme.error }}>
              <span style={{ ...styles.tradingBadgeText, color: theme.text }}>Weekly Trading & Tournaments</span>
            </div>
          )}
        </div>
        {/* Only show follow button for Basic+ tiers */}
        {currentUserId && tier >= 1 && (
          <button
            style={{
              ...styles.followButton,
              borderColor: theme.accent,
              color: theme.accent,
              ...(followerData?.is_following ? { backgroundColor: theme.accent, color: '#fff' } : {}),
            }}
            onClick={(e) => {
              e.stopPropagation();
              onToggleFollow(location.id);
            }}
          >
            {followerData?.is_following ? (
              <span style={styles.followingText}>Following</span>
            ) : (
              <>
                <PlusIcon />
                <span style={styles.followText}>Follow</span>
              </>
            )}
          </button>
        )}
      </div>
      {/* Only show stats for Basic+ tiers */}
      {tier >= 1 && (
        <div style={styles.statsRow}>
          <div style={styles.statItem}>
            <StarIcon />
            <span style={{ ...styles.statText, color: theme.text }}>
              {rating ? `${rating.avg_rating.toFixed(1)} (${rating.rating_count})` : 'Not Rated'}
            </span>
          </div>
          <div style={{ ...styles.statDivider, backgroundColor: theme.border }} />
          <div style={styles.statItem}>
            <UsersIcon />
            <span style={{ ...styles.statText, color: theme.text }}>
              {followerData?.follower_count || 0} followers
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

// Event Card Component
const EventCard = ({ event, isSponsored }) => {
  const { theme, isDark } = useTheme();

  return (
    <div style={{
      ...styles.eventCard,
      backgroundColor: theme.bgSheet,
      borderColor: theme.border,
      ...(isSponsored ? { backgroundColor: isDark ? '#1a1033' : '#f3e8ff', borderColor: theme.purple } : {}),
    }}>
    {isSponsored && (
      <div style={styles.sponsoredBadge}>
        <span style={{ ...styles.sponsoredIcon, color: theme.purple }}>*</span>
        <span style={{ ...styles.sponsoredText, color: theme.purple }}>SPONSORED EVENT</span>
      </div>
    )}
    <div style={styles.eventContent}>
      <div style={{ ...styles.eventImageContainer, backgroundColor: theme.border }}>
        <span style={{ ...styles.eventImageText, color: theme.text }}>{event.image || 'EVT'}</span>
      </div>
      <div style={styles.eventDetails}>
        <span style={{ ...styles.eventTitle, color: theme.text }}>{event.title}</span>
        <span style={{ ...styles.eventLocation, color: theme.textMuted }}>{event.location}</span>
        <div style={styles.eventMetaRow}>
          <div style={{ ...styles.eventMeta, color: theme.textSecondary }}>
            <CalendarIcon />
            <span style={styles.eventMetaText}>{event.date}</span>
          </div>
          <div style={{ ...styles.eventMeta, color: theme.textSecondary }}>
            <UsersIcon />
            <span style={styles.eventMetaText}>{event.attendees} going</span>
          </div>
        </div>
        <div style={styles.eventFooter}>
          <div style={{
            ...styles.eventTypeBadge,
            ...(event.type === 'External Venue' ? styles.externalVenueBadge : styles.storeEventBadge),
          }}>
            <span style={{
              ...styles.eventTypeText,
              color: event.type === 'External Venue' ? theme.purple : theme.success,
            }}>
              {event.type}
            </span>
          </div>
          <span style={{ ...styles.entryFee, color: theme.accent }}>{event.entryFee}</span>
        </div>
      </div>
    </div>
  </div>
  );
};

// Following Card Component
const FollowingCard = ({ location, onUnfollow }) => {
  const { theme } = useTheme();

  return (
  <div style={{ ...styles.followingCard, backgroundColor: theme.bgSheet, borderColor: theme.border }}>
    <div style={styles.followingHeader}>
      <div style={{ ...styles.followingImageContainer, backgroundColor: theme.border }}>
        <span style={{ ...styles.followingImageText, color: theme.text }}>
          {location.store_name?.substring(0, 2).toUpperCase() || 'LS'}
        </span>
      </div>
      <div style={styles.followingInfo}>
        <span style={{ ...styles.followingName, color: theme.text }}>{location.store_name}</span>
        <div style={styles.followingMetaRow}>
          <div style={{ ...styles.followingMeta, color: theme.textSecondary }}>
            <MapPinIcon />
            <span style={styles.followingMetaText}>{location.city}, {location.state}</span>
          </div>
        </div>
      </div>
      <button style={{ ...styles.unfollowButton, backgroundColor: `${theme.error}1A`, color: theme.error }} onClick={() => onUnfollow(location.id)}>
        <HeartIcon filled={true} />
      </button>
    </div>
    {location.avg_rating && (
      <div style={{ ...styles.followingRatings, borderTopColor: theme.border }}>
        <div style={styles.ratingItem}>
          <span style={{ ...styles.ratingLabel, color: theme.textSecondary }}>Rating</span>
          <div style={styles.ratingValue}>
            <StarIcon />
            <span>{location.avg_rating?.toFixed(1) || 'N/A'}</span>
          </div>
        </div>
      </div>
    )}
  </div>
  );
};

// Skeleton Components
const LocationSkeleton = () => {
  const { theme } = useTheme();

  return (
  <div style={{ ...styles.skeletonCard, backgroundColor: theme.bgSheet, borderColor: theme.border }}>
    <div style={styles.skeletonHeader}>
      <div style={{ ...styles.skeletonTitle, backgroundColor: theme.bgCard }} />
      <div style={{ ...styles.skeletonButton, backgroundColor: theme.bgCard }} />
    </div>
    <div style={{ ...styles.skeletonAddress, backgroundColor: theme.bgCard }} />
    <div style={{ ...styles.skeletonStats, backgroundColor: theme.bgCard }} />
  </div>
  );
};

// Radius Edit Modal Component
const RadiusEditModal = ({ isOpen, onClose, radius, onRadiusChange }) => {
  const { theme } = useTheme();
  const [tempRadius, setTempRadius] = useState(radius);

  if (!isOpen) return null;

  return (
    <>
      <div style={{ ...radiusModalStyles.backdrop }} onClick={onClose} />
      <div style={{ ...radiusModalStyles.modal, backgroundColor: theme.bg, borderColor: theme.border }}>
        <div style={{ ...radiusModalStyles.header, borderBottomColor: theme.bgCard }}>
          <h3 style={{ ...radiusModalStyles.title, color: theme.text }}>Search Radius</h3>
          <button style={{ ...radiusModalStyles.closeButton, backgroundColor: theme.bgCard, color: theme.textSecondary }} onClick={onClose}>
            <CloseIcon />
          </button>
        </div>
        <div style={radiusModalStyles.content}>
          <div style={radiusModalStyles.radiusHeader}>
            <span style={{ ...radiusModalStyles.radiusLabel, color: theme.textMuted }}>Distance</span>
            <span style={{ ...radiusModalStyles.radiusValue, color: theme.accent }}>{tempRadius} miles</span>
          </div>
          <input
            type="range"
            min="5"
            max="200"
            step="5"
            value={tempRadius}
            onChange={(e) => setTempRadius(parseInt(e.target.value))}
            style={{ ...radiusModalStyles.slider, background: theme.border }}
          />
          <div style={{ ...radiusModalStyles.radiusMarks, color: theme.textSecondary }}>
            <span>5 mi</span>
            <span>100 mi</span>
            <span>200 mi</span>
          </div>
        </div>
        <div style={{ ...radiusModalStyles.footer, borderTopColor: theme.bgCard }}>
          <button
            style={{ ...radiusModalStyles.applyButton, backgroundColor: theme.accent, color: '#fff' }}
            onClick={() => {
              onRadiusChange(tempRadius);
              onClose();
            }}
          >
            Apply
          </button>
        </div>
      </div>
    </>
  );
};

const radiusModalStyles = {
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    zIndex: 1000,
  },
  modal: {
    position: 'fixed',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: '340px',
    borderRadius: '16px',
    zIndex: 1001,
    border: '1px solid',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: '1px solid',
  },
  title: {
    fontSize: '16px',
    fontWeight: '600',
    margin: 0,
  },
  closeButton: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  content: {
    padding: '20px',
  },
  radiusHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  radiusLabel: {
    fontSize: '13px',
  },
  radiusValue: {
    fontSize: '14px',
    fontWeight: '600',
  },
  slider: {
    width: '100%',
    height: '6px',
    borderRadius: '3px',
    appearance: 'none',
    cursor: 'pointer',
    WebkitAppearance: 'none',
  },
  radiusMarks: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '8px',
    fontSize: '11px',
  },
  footer: {
    padding: '16px 20px',
    borderTop: '1px solid',
  },
  applyButton: {
    width: '100%',
    padding: '12px',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
};

// Main Locations Component
const Locations = () => {
  const { theme, isDark } = useTheme();
  const { user } = useAuth();
  const { activeProfile } = useProfile();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('featured'); // Featured is default

  // Search state
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTab, setSearchTab] = useState('all'); // 'all', 'shops', 'events'
  const [searchResults, setSearchResults] = useState({ shops: [], events: [] });
  const [searchLoading, setSearchLoading] = useState(false);
  const searchInputRef = useRef(null);

  // Suggestion modal state
  const [suggestModalOpen, setSuggestModalOpen] = useState(false);
  const [suggestTab, setSuggestTab] = useState('store'); // 'store' or 'event'
  const [suggestSubmitting, setSuggestSubmitting] = useState(false);
  const [suggestSuccess, setSuggestSuccess] = useState(false);
  const [storeForm, setStoreForm] = useState({
    storeName: '', address: '', city: '', state: '', zipCode: '',
    phone: '', website: '', isOwner: null, relationship: '', isNearby: null, notes: ''
  });
  const [eventForm, setEventForm] = useState({
    eventName: '', isConfirmed: null, isOrganizer: null, sourceType: '', sourceLink: '',
    frequency: null, venueName: '', address: '', city: '', state: '',
    eventDate: '', eventTime: '', description: '', expectedVendors: '', notes: ''
  });
  const [locations, setLocations] = useState([]);
  const [ratings, setRatings] = useState({});
  const [followers, setFollowers] = useState({});
  const [followingLocations, setFollowingLocations] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [searchRadius, setSearchRadius] = useState(50);
  const [radiusModalOpen, setRadiusModalOpen] = useState(false);

  // Waitlist state
  const [waitlistCounter, setWaitlistCounter] = useState(null);
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistStore, setWaitlistStore] = useState('');
  const [waitlistZip, setWaitlistZip] = useState('');
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);
  const [waitlistSubmitted, setWaitlistSubmitted] = useState(false);
  const [waitlistExpanded, setWaitlistExpanded] = useState(false);

  // Fetch waitlist counter
  useEffect(() => {
    const fetchCounter = async () => {
      const { data } = await supabase.from('waitlist_counter').select('*').eq('id', 1).single();
      if (data) setWaitlistCounter(data);
    };
    fetchCounter();
  }, []);

  const handleWaitlistSubmit = async () => {
    if (!waitlistEmail.trim() || !waitlistStore.trim() || waitlistSubmitting) return;
    setWaitlistSubmitting(true);
    try {
      await supabase.from('location_waitlist').insert({
        store_name: waitlistStore.trim(),
        email: waitlistEmail.trim(),
        zip_code: waitlistZip.trim() || null,
      });
      setWaitlistSubmitted(true);
    } catch (err) {
      console.error('Waitlist signup error:', err);
    } finally {
      setWaitlistSubmitting(false);
    }
  };

  // Waitlist CTA component (reused across tabs)
  const WaitlistCTA = () => {
    if (!waitlistCounter?.is_active) return null;

    // Success state
    if (waitlistSubmitted) {
      return (
        <div style={{
          marginTop: 24, padding: 24, borderRadius: 16, textAlign: 'center',
          background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.08), rgba(139, 92, 246, 0.08))',
          border: '1px solid rgba(14, 165, 233, 0.2)',
        }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>You're on the list!</div>
          <p style={{ fontSize: 14, color: theme.textSecondary, margin: 0 }}>
            We'll reach out when it's time to get your store set up.
          </p>
        </div>
      );
    }

    // Collapsed: glowing teaser banner
    if (!waitlistExpanded) {
      return (
        <button
          onClick={() => setWaitlistExpanded(true)}
          style={{
            marginTop: 24, padding: '20px 20px', borderRadius: 16, width: '100%',
            border: 'none', cursor: 'pointer', textAlign: 'center', position: 'relative',
            overflow: 'hidden',
            background: isDark
              ? 'linear-gradient(135deg, #0c1929 0%, #1a1040 50%, #0c1929 100%)'
              : 'linear-gradient(135deg, #eff6ff 0%, #f0e6ff 50%, #eff6ff 100%)',
            boxShadow: isDark
              ? '0 0 20px rgba(14, 165, 233, 0.15), 0 0 40px rgba(139, 92, 246, 0.1), inset 0 1px 0 rgba(255,255,255,0.05)'
              : '0 0 20px rgba(14, 165, 233, 0.1), 0 2px 8px rgba(0,0,0,0.06)',
          }}
        >
          {/* Glow pulse */}
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            borderRadius: 16,
            border: isDark ? '1px solid rgba(14, 165, 233, 0.25)' : '1px solid rgba(14, 165, 233, 0.15)',
            animation: 'waitlistGlow 3s ease-in-out infinite',
          }} />

          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{
              fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5,
              color: '#0ea5e9', marginBottom: 6,
            }}>
              Coming Soon
            </div>
            <div style={{
              fontSize: 18, fontWeight: 700, color: theme.text, marginBottom: 6, lineHeight: 1.3,
            }}>
              More locations are on the way
            </div>
            <div style={{
              fontSize: 13, color: theme.textSecondary, lineHeight: 1.4, marginBottom: 12,
            }}>
              Get your shop on CardChase before everyone else.
              {waitlistCounter.show_counter && waitlistCounter.display_count > 0 && (
                <span style={{ fontWeight: 700, color: '#0ea5e9' }}>
                  {' '}{waitlistCounter.display_count} shops already waiting.
                </span>
              )}
            </div>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 16px', borderRadius: 20,
              backgroundColor: '#0ea5e9', color: '#fff',
              fontSize: 13, fontWeight: 600,
            }}>
              Join the Waiting List
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </div>
          </div>
        </button>
      );
    }

    // Expanded: full screen modal popup
    return (
      <>
        {/* Keep the banner visible behind */}
        <div style={{ marginTop: 24, opacity: 0.3, pointerEvents: 'none' }}>
          <div style={{
            padding: '20px', borderRadius: 16, textAlign: 'center',
            background: isDark
              ? 'linear-gradient(135deg, #0c1929 0%, #1a1040 50%, #0c1929 100%)'
              : 'linear-gradient(135deg, #eff6ff 0%, #f0e6ff 50%, #eff6ff 100%)',
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5, color: '#0ea5e9' }}>Coming Soon</div>
          </div>
        </div>

        {/* Modal overlay */}
        <div
          onClick={() => setWaitlistExpanded(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            backgroundColor: 'rgba(0,0,0,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: 380, padding: 24, borderRadius: 20,
              background: isDark
                ? 'linear-gradient(135deg, #0c1929 0%, #1a1040 50%, #0c1929 100%)'
                : 'linear-gradient(135deg, #f8faff 0%, #f3eeff 50%, #f8faff 100%)',
              boxShadow: '0 0 40px rgba(14, 165, 233, 0.2), 0 0 80px rgba(139, 92, 246, 0.15)',
              border: isDark ? '1px solid rgba(14, 165, 233, 0.25)' : '1px solid rgba(14, 165, 233, 0.15)',
            }}
          >
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5, color: '#0ea5e9', marginBottom: 6 }}>
                Waiting List
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, color: theme.text, marginBottom: 6 }}>
                Get your shop on CardChase
              </div>
              <div style={{ fontSize: 13, color: theme.textSecondary, lineHeight: 1.4 }}>
                Be one of the first locations on the platform.
              </div>
              {waitlistCounter.show_counter && waitlistCounter.display_count > 0 && (
                <div style={{
                  marginTop: 12, padding: '8px 16px', borderRadius: 20, display: 'inline-block',
                  backgroundColor: isDark ? 'rgba(14, 165, 233, 0.1)' : 'rgba(14, 165, 233, 0.06)',
                  border: '1px solid rgba(14, 165, 233, 0.2)',
                }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#0ea5e9' }}>
                    {waitlistCounter.display_count} shops already waiting
                  </span>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <input
                type="text" placeholder="Store name" value={waitlistStore}
                onChange={e => setWaitlistStore(e.target.value)} autoFocus
                style={{
                  padding: '14px 16px', borderRadius: 12, fontSize: 15,
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                  backgroundColor: isDark ? 'rgba(0,0,0,0.3)' : '#fff',
                  color: theme.text, outline: 'none',
                }}
              />
              <input
                type="email" placeholder="Contact email" value={waitlistEmail}
                onChange={e => setWaitlistEmail(e.target.value)}
                style={{
                  padding: '14px 16px', borderRadius: 12, fontSize: 15,
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                  backgroundColor: isDark ? 'rgba(0,0,0,0.3)' : '#fff',
                  color: theme.text, outline: 'none',
                }}
              />
              <input
                type="text" placeholder="Zip code (optional)" value={waitlistZip}
                onChange={e => setWaitlistZip(e.target.value)}
                style={{
                  padding: '14px 16px', borderRadius: 12, fontSize: 15,
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                  backgroundColor: isDark ? 'rgba(0,0,0,0.3)' : '#fff',
                  color: theme.text, outline: 'none',
                }}
              />
              <button
                onClick={handleWaitlistSubmit}
                disabled={!waitlistEmail.trim() || !waitlistStore.trim() || waitlistSubmitting}
                style={{
                  padding: 16, borderRadius: 14, border: 'none', marginTop: 4,
                  background: 'linear-gradient(135deg, #0ea5e9, #8b5cf6)',
                  color: '#fff', fontSize: 16, fontWeight: 700, cursor: 'pointer',
                  opacity: (!waitlistEmail.trim() || !waitlistStore.trim() || waitlistSubmitting) ? 0.5 : 1,
                  boxShadow: '0 4px 16px rgba(14, 165, 233, 0.35)',
                }}
              >
                {waitlistSubmitting ? 'Submitting...' : 'Join the Waiting List'}
              </button>
            </div>

            <button
              onClick={() => setWaitlistExpanded(false)}
              style={{
                width: '100%', marginTop: 12, padding: 10, background: 'none',
                border: 'none', color: theme.textMuted, fontSize: 13, cursor: 'pointer',
              }}
            >
              Maybe later
            </button>
          </div>
        </div>
      </>
    );
  };

  // Handle location card click - navigate to detail page
  const handleLocationClick = (location) => {
    navigate(`/location/${location.id}`);
  };

  // Set currentUserId and userProfile from activeProfile (supports child profile when parent views as child)
  useEffect(() => {
    if (!activeProfile?.id) {
      setCurrentUserId(null);
      setUserProfile(null);
      return;
    }

    setCurrentUserId(activeProfile.id);
    setUserProfile({
      latitude: activeProfile.latitude,
      longitude: activeProfile.longitude,
      city: activeProfile.location_city,
      state: activeProfile.location_state,
    });
  }, [activeProfile?.id, activeProfile?.latitude, activeProfile?.longitude, activeProfile?.location_city, activeProfile?.location_state]);

  // Handle search param from URL
  useEffect(() => {
    if (searchParams.get('search') === 'true') {
      setSearchOpen(true);
      // Clear the param without navigation
      setSearchParams({}, { replace: true });
      // Focus input after sheet opens
      setTimeout(() => searchInputRef.current?.focus(), 300);
    }
  }, [searchParams, setSearchParams]);

  // Close search sheet
  const closeSearch = () => {
    setSearchOpen(false);
    setSearchQuery('');
    setSearchResults({ shops: [], events: [] });
  };

  // Perform search
  const performSearch = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults({ shops: [], events: [] });
      return;
    }

    setSearchLoading(true);
    try {
      // Search locations
      const { data: shopsData } = await supabase
        .from('locations')
        .select('id, store_name, address, city, state, latitude, longitude, subscription_tier, verified')
        .eq('visible_on_app', true)
        .or(`store_name.ilike.%${query}%,city.ilike.%${query}%,state.ilike.%${query}%`)
        .order('subscription_tier', { ascending: false })
        .limit(20);

      // Search events
      const { data: eventsData } = await supabase
        .from('location_events')
        .select(`
          id, event_name, event_date, event_time, description,
          locations!inner(id, store_name, city, state, latitude, longitude, subscription_tier)
        `)
        .ilike('event_name', `%${query}%`)
        .gte('event_date', new Date().toISOString().split('T')[0])
        .order('event_date', { ascending: true })
        .limit(20);

      // Add distance to results if user has location
      const shopsWithDistance = (shopsData || []).map(shop => ({
        ...shop,
        distance: userProfile?.latitude && shop.latitude
          ? calculateDistance(userProfile.latitude, userProfile.longitude, shop.latitude, shop.longitude)
          : null
      }));

      const eventsWithDistance = (eventsData || []).map(event => ({
        ...event,
        distance: userProfile?.latitude && event.locations?.latitude
          ? calculateDistance(userProfile.latitude, userProfile.longitude, event.locations.latitude, event.locations.longitude)
          : null
      }));

      setSearchResults({
        shops: shopsWithDistance,
        events: eventsWithDistance
      });
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setSearchLoading(false);
    }
  }, [userProfile]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery) performSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, performSearch]);

  // Close suggestion modal and reset
  const closeSuggestModal = () => {
    setSuggestModalOpen(false);
    setSuggestSuccess(false);
    setSuggestTab('store');
    setStoreForm({
      storeName: '', address: '', city: '', state: '', zipCode: '',
      phone: '', website: '', isOwner: null, relationship: '', isNearby: null, notes: ''
    });
    setEventForm({
      eventName: '', isConfirmed: null, isOrganizer: null, sourceType: '', sourceLink: '',
      frequency: null, venueName: '', address: '', city: '', state: '',
      eventDate: '', eventTime: '', description: '', expectedVendors: '', notes: ''
    });
  };

  // Submit store suggestion
  const submitStoreSuggestion = async () => {
    if (!storeForm.storeName || !storeForm.city || !storeForm.state) return;

    setSuggestSubmitting(true);
    try {
      const { error } = await supabase
        .from('location_suggestions')
        .insert({
          submitted_by: currentUserId, // Hidden tracking
          store_name: storeForm.storeName,
          address: storeForm.address || null,
          city: storeForm.city,
          state: storeForm.state.toUpperCase(),
          zip_code: storeForm.zipCode || null,
          phone: storeForm.phone || null,
          website: storeForm.website || null,
          is_owner: storeForm.isOwner === true,
          relationship: storeForm.relationship || null,
          discovery_method: storeForm.isNearby || null,
          notes: storeForm.notes || null,
        });

      if (error) throw error;
      setSuggestSuccess(true);
    } catch (error) {
      console.error('Error submitting store suggestion:', error);
      alert('Failed to submit. Please try again.');
    } finally {
      setSuggestSubmitting(false);
    }
  };

  // Submit event suggestion
  const submitEventSuggestion = async () => {
    if (!eventForm.eventName || !eventForm.city || !eventForm.state || !eventForm.sourceLink) return;

    setSuggestSubmitting(true);
    try {
      const { error } = await supabase
        .from('event_suggestions')
        .insert({
          submitted_by: currentUserId, // Hidden tracking
          event_name: eventForm.eventName,
          is_confirmed: eventForm.isConfirmed,
          is_organizer: eventForm.isOrganizer === true,
          source_type: eventForm.sourceType || null,
          source_link: eventForm.sourceLink || null,
          frequency: eventForm.frequency || null,
          venue_name: eventForm.venueName || null,
          address: eventForm.address || null,
          city: eventForm.city,
          state: eventForm.state.toUpperCase(),
          event_date: eventForm.eventDate || null,
          event_time: eventForm.eventTime || null,
          description: eventForm.description || null,
          expected_vendors: eventForm.expectedVendors ? parseInt(eventForm.expectedVendors) : null,
          notes: eventForm.notes || null,
        });

      if (error) throw error;
      setSuggestSuccess(true);
    } catch (error) {
      console.error('Error submitting event suggestion:', error);
      alert('Failed to submit. Please try again.');
    } finally {
      setSuggestSubmitting(false);
    }
  };

  // Fetch locations
  const fetchLocations = useCallback(async () => {
    try {
      setLoading(true);

      const { data: locationsData, error } = await supabase
        .from('locations')
        .select('id, store_name, address, city, state, zip_code, latitude, longitude, verified, subscription_tier, visible_on_app')
        .eq('verified', true)
        .eq('visible_on_app', true);

      console.log('Locations query result:', { locationsData, error });

      if (error) {
        console.error('Error fetching locations:', error);
        return;
      }

      // Sort by tier then name
      const sortedLocations = (locationsData || []).sort((a, b) => {
        const tierDiff = (b.subscription_tier || 0) - (a.subscription_tier || 0);
        if (tierDiff !== 0) return tierDiff;
        return a.store_name.localeCompare(b.store_name);
      });

      setLocations(sortedLocations);

      // Fetch ratings (using new schema with cleanliness, friendliness, space)
      const locationIds = sortedLocations.map(l => l.id);
      if (locationIds.length > 0) {
        const { data: ratingsData } = await supabase
          .from('location_ratings')
          .select('location_id, cleanliness_rating, friendliness_rating, space_rating')
          .in('location_id', locationIds);

        const ratingsMap = {};
        ratingsData?.forEach(r => {
          if (!ratingsMap[r.location_id]) {
            ratingsMap[r.location_id] = { total: 0, count: 0, reviews: 0 };
          }
          // Average the three rating types per review
          if (r.cleanliness_rating) { ratingsMap[r.location_id].total += r.cleanliness_rating; ratingsMap[r.location_id].count++; }
          if (r.friendliness_rating) { ratingsMap[r.location_id].total += r.friendliness_rating; ratingsMap[r.location_id].count++; }
          if (r.space_rating) { ratingsMap[r.location_id].total += r.space_rating; ratingsMap[r.location_id].count++; }
          ratingsMap[r.location_id].reviews++;
        });

        const avgRatings = {};
        Object.keys(ratingsMap).forEach(id => {
          avgRatings[id] = {
            avg_rating: ratingsMap[id].count > 0 ? ratingsMap[id].total / ratingsMap[id].count : 0,
            rating_count: ratingsMap[id].reviews,
          };
        });
        setRatings(avgRatings);

        // Fetch follower counts
        const { data: followerCounts } = await supabase
          .from('location_followers')
          .select('location_id')
          .in('location_id', locationIds);

        const counts = {};
        followerCounts?.forEach(f => {
          counts[f.location_id] = (counts[f.location_id] || 0) + 1;
        });

        // Check user's follows
        let userFollows = {};
        if (currentUserId) {
          const { data: userFollowData } = await supabase
            .from('location_followers')
            .select('location_id')
            .eq('follower_id', currentUserId)
            .in('location_id', locationIds);

          userFollowData?.forEach(f => {
            userFollows[f.location_id] = true;
          });
        }

        const followersMap = {};
        locationIds.forEach(id => {
          followersMap[id] = {
            follower_count: counts[id] || 0,
            is_following: userFollows[id] || false,
          };
        });
        setFollowers(followersMap);
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUserId]);

  // Fetch following locations
  const fetchFollowingLocations = useCallback(async () => {
    if (!currentUserId) {
      setFollowingLocations([]);
      return;
    }

    try {
      const { data: followData } = await supabase
        .from('location_followers')
        .select('location_id')
        .eq('follower_id', currentUserId);

      if (!followData || followData.length === 0) {
        setFollowingLocations([]);
        return;
      }

      const locationIds = followData.map(f => f.location_id);
      const { data: locationsData } = await supabase
        .from('locations')
        .select('*')
        .in('id', locationIds);

      setFollowingLocations(locationsData || []);
    } catch (err) {
      console.error('Error fetching following:', err);
    }
  }, [currentUserId]);

  useEffect(() => {
    if (currentUserId !== null || isGuest()) {
      fetchLocations();
    }
  }, [currentUserId, fetchLocations]);

  useEffect(() => {
    if (activeTab === 'following' && currentUserId) {
      fetchFollowingLocations();
    }
  }, [activeTab, currentUserId, fetchFollowingLocations]);

  // Fetch events
  const fetchEvents = useCallback(async () => {
    try {
      setEventsLoading(true);

      // Fetch active events, ordered by date
      const { data: eventsData, error } = await supabase
        .from('events')
        .select('*')
        .eq('is_active', true)
        .gte('event_date', new Date().toISOString().split('T')[0])
        .order('is_sponsored', { ascending: false })
        .order('event_date', { ascending: true })
        .limit(20);

      if (error) {
        console.error('Error fetching events:', error);
        // If table doesn't exist, use sample data
        if (error.code === '42P01') {
          setEvents(getSampleEvents());
        }
        return;
      }

      if (!eventsData || eventsData.length === 0) {
        // Use sample events if no data
        setEvents(getSampleEvents());
        return;
      }

      // Format events
      const formattedEvents = eventsData.map(evt => ({
        id: evt.id,
        title: evt.title,
        location: evt.location_name,
        date: formatEventDate(evt.event_date, evt.event_end_date),
        time: formatEventTime(evt.start_time, evt.end_time),
        type: evt.event_type || 'Store Event',
        entryFee: evt.entry_fee || 'Free',
        image: evt.image_code || evt.title?.substring(0, 3).toUpperCase() || 'EVT',
        isSponsored: evt.is_sponsored,
        attendees: 0, // Will be updated below
      }));

      setEvents(formattedEvents);

      // Fetch attendee counts
      const eventIds = formattedEvents.map(e => e.id);
      if (eventIds.length > 0) {
        const { data: attendeesData } = await supabase
          .from('event_attendees')
          .select('event_id')
          .in('event_id', eventIds)
          .eq('status', 'going');

        const counts = {};
        attendeesData?.forEach(a => {
          counts[a.event_id] = (counts[a.event_id] || 0) + 1;
        });

        // Update events with attendee counts
        setEvents(prev => prev.map(evt => ({
          ...evt,
          attendees: counts[evt.id] || 0,
        })));
      }
    } catch (err) {
      console.error('Error fetching events:', err);
      setEvents(getSampleEvents());
    } finally {
      setEventsLoading(false);
    }
  }, []);

  // Sample events fallback
  const getSampleEvents = () => [
    {
      id: 'sample-1',
      title: "Regional Pokemon Tournament",
      location: "Convention Center Downtown",
      date: "Coming Soon",
      time: "9AM - 7PM",
      type: "Tournament",
      attendees: 0,
      image: "TRN",
      entryFee: "$25",
      isSponsored: true
    },
    {
      id: 'sample-2',
      title: "Beginner Trading Workshop",
      location: "Local Community Center",
      date: "Coming Soon",
      time: "6PM - 8PM",
      type: "Meetup",
      attendees: 0,
      image: "WKS",
      entryFee: "$10",
      isSponsored: true
    },
    {
      id: 'sample-3',
      title: "Pokemon Card Expo",
      location: "City Sports Complex",
      date: "Coming Soon",
      time: "10AM - 6PM",
      type: "External Venue",
      attendees: 0,
      image: "EXP",
      entryFee: "$15",
      isSponsored: false
    },
    {
      id: 'sample-4',
      title: "Monthly Trading Meetup",
      location: "Public Library Main Branch",
      date: "Coming Soon",
      time: "2PM - 5PM",
      type: "Store Event",
      attendees: 0,
      image: "MTU",
      entryFee: "Free",
      isSponsored: false
    }
  ];

  // Format event date
  const formatEventDate = (startDate, endDate) => {
    if (!startDate) return 'TBD';
    const start = new Date(startDate);
    const options = { month: 'short', day: 'numeric' };

    if (endDate && endDate !== startDate) {
      const end = new Date(endDate);
      if (start.getMonth() === end.getMonth()) {
        return `${start.toLocaleDateString('en-US', { month: 'short' })} ${start.getDate()}-${end.getDate()}`;
      }
      return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}`;
    }
    return start.toLocaleDateString('en-US', options);
  };

  // Format event time
  const formatEventTime = (startTime, endTime) => {
    if (!startTime) return '';
    const formatTime = (time) => {
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours);
      const period = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      return `${displayHour}${minutes !== '00' ? ':' + minutes : ''}${period}`;
    };

    if (endTime) {
      return `${formatTime(startTime)} - ${formatTime(endTime)}`;
    }
    return formatTime(startTime);
  };

  // Fetch events when tab changes to events
  useEffect(() => {
    if (activeTab === 'events') {
      fetchEvents();
    }
  }, [activeTab, fetchEvents]);

  // Toggle follow
  const handleToggleFollow = async (locationId) => {
    if (!currentUserId) return;

    const currentFollowStatus = followers[locationId]?.is_following || false;

    // Optimistic update
    setFollowers(prev => ({
      ...prev,
      [locationId]: {
        ...prev[locationId],
        is_following: !currentFollowStatus,
        follower_count: currentFollowStatus
          ? (prev[locationId]?.follower_count || 1) - 1
          : (prev[locationId]?.follower_count || 0) + 1,
      },
    }));

    try {
      if (currentFollowStatus) {
        await supabase
          .from('location_followers')
          .delete()
          .eq('follower_id', currentUserId)
          .eq('location_id', locationId);
      } else {
        await supabase
          .from('location_followers')
          .insert({ follower_id: currentUserId, location_id: locationId });
      }
    } catch (err) {
      console.error('Error toggling follow:', err);
      // Revert
      setFollowers(prev => ({
        ...prev,
        [locationId]: {
          ...prev[locationId],
          is_following: currentFollowStatus,
          follower_count: !currentFollowStatus
            ? (prev[locationId]?.follower_count || 1) - 1
            : (prev[locationId]?.follower_count || 0) + 1,
        },
      }));
    }
  };

  // Unfollow from following tab
  const handleUnfollow = async (locationId) => {
    await handleToggleFollow(locationId);
    setFollowingLocations(prev => prev.filter(l => l.id !== locationId));
  };

  // Calculate distance and filter/sort locations
  const filteredLocations = locations
    .map(loc => ({
      ...loc,
      distance: userProfile?.latitude && userProfile?.longitude && loc.latitude && loc.longitude
        ? calculateDistance(userProfile.latitude, userProfile.longitude, parseFloat(loc.latitude), parseFloat(loc.longitude))
        : null,
    }))
    .filter(loc =>
      // Filter by radius if user has location
      (userProfile?.latitude && loc.distance !== null ? loc.distance <= searchRadius : true)
    )
    .sort((a, b) => {
      // Sort by distance first (if available), then by tier
      if (a.distance !== null && b.distance !== null) {
        return a.distance - b.distance;
      }
      if (a.distance !== null) return -1;
      if (b.distance !== null) return 1;
      return (b.subscription_tier || 0) - (a.subscription_tier || 0);
    });

  const filteredEvents = events;

  // Generate dynamic styles based on theme
  const getDynamicStyles = () => ({
    container: { ...styles.container, backgroundColor: theme.bg },
    header: { ...styles.header, backgroundColor: theme.bg },
    headerTitle: { ...styles.headerTitle, color: theme.text },
    iconButton: { ...styles.iconButton, backgroundColor: theme.bgCard, borderColor: theme.border, color: theme.textMuted },
    tabContainer: { ...styles.tabContainer, backgroundColor: theme.bgCard },
    activeTab: { ...styles.activeTab, backgroundColor: theme.accent },
    tabText: { ...styles.tabText, color: theme.textSecondary },
    activeTabText: { ...styles.activeTabText, color: '#fff' },
    locationBanner: { ...styles.locationBanner, backgroundColor: theme.bgSheet, borderColor: theme.border },
    locationBannerContent: { ...styles.locationBannerContent, color: theme.textMuted },
    locationBannerText: { ...styles.locationBannerText, color: theme.text },
    editRadiusButton: { ...styles.editRadiusButton, color: theme.accent },
    sectionTitle: { ...styles.sectionTitle, color: theme.text },
    sectionSubtitle: { ...styles.sectionSubtitle, color: theme.textSecondary },
    locationCard: { ...styles.locationCard, backgroundColor: theme.bgSheet },
    locationName: { ...styles.locationName, color: theme.text },
    locationAddress: { ...styles.locationAddress },
    distanceBadge: { ...styles.distanceBadge, backgroundColor: `${theme.accent}26`, color: theme.accent },
    tradingBadge: { ...styles.tradingBadge, backgroundColor: theme.error },
    tradingBadgeText: { ...styles.tradingBadgeText, color: theme.text },
    followButton: { ...styles.followButton, borderColor: theme.accent, color: theme.accent },
    followingButton: { ...styles.followingButton, backgroundColor: theme.accent, color: '#fff' },
    statText: { ...styles.statText, color: theme.text },
    statDivider: { ...styles.statDivider, backgroundColor: theme.border },
    eventCard: { ...styles.eventCard, backgroundColor: theme.bgSheet, borderColor: theme.border },
    sponsoredEventCard: { ...styles.sponsoredEventCard, backgroundColor: isDark ? '#1a1033' : '#f3e8ff', borderColor: theme.purple },
    sponsoredIcon: { ...styles.sponsoredIcon, color: theme.purple },
    sponsoredText: { ...styles.sponsoredText, color: theme.purple },
    eventImageContainer: { ...styles.eventImageContainer, backgroundColor: theme.border },
    eventImageText: { ...styles.eventImageText, color: theme.text },
    eventTitle: { ...styles.eventTitle, color: theme.text },
    eventLocation: { ...styles.eventLocation, color: theme.textMuted },
    eventMeta: { ...styles.eventMeta, color: theme.textSecondary },
    eventTypeText: { ...styles.eventTypeText },
    externalVenueText: { ...styles.externalVenueText, color: theme.purple },
    storeEventText: { ...styles.storeEventText, color: theme.success },
    entryFee: { ...styles.entryFee, color: theme.accent },
    loadMoreButton: { ...styles.loadMoreButton, borderColor: theme.border },
    loadMoreText: { ...styles.loadMoreText, color: theme.textMuted },
    followingCard: { ...styles.followingCard, backgroundColor: theme.bgSheet, borderColor: theme.border },
    followingImageContainer: { ...styles.followingImageContainer, backgroundColor: theme.border },
    followingImageText: { ...styles.followingImageText, color: theme.text },
    followingName: { ...styles.followingName, color: theme.text },
    followingMeta: { ...styles.followingMeta, color: theme.textSecondary },
    unfollowButton: { ...styles.unfollowButton, backgroundColor: `${theme.error}1A`, color: theme.error },
    followingRatings: { ...styles.followingRatings, borderTopColor: theme.border },
    ratingLabel: { ...styles.ratingLabel, color: theme.textSecondary },
    findNewButton: { ...styles.findNewButton, backgroundColor: theme.accent },
    findNewText: { ...styles.findNewText, color: theme.text },
    emptyText: { ...styles.emptyText, color: theme.textSecondary },
    emptySubtext: { ...styles.emptySubtext, color: theme.textMuted },
    skeletonCard: { ...styles.skeletonCard, backgroundColor: theme.bgSheet, borderColor: theme.border },
    skeletonTitle: { ...styles.skeletonTitle, backgroundColor: theme.bgCard },
    skeletonButton: { ...styles.skeletonButton, backgroundColor: theme.bgCard },
    skeletonAddress: { ...styles.skeletonAddress, backgroundColor: theme.bgCard },
    skeletonStats: { ...styles.skeletonStats, backgroundColor: theme.bgCard },
  });

  const dynamicStyles = getDynamicStyles();

  return (
    <div style={dynamicStyles.container}>
      {/* Header */}
      <div style={dynamicStyles.header}>
        <h1 style={dynamicStyles.headerTitle}>Discover</h1>
        <button style={dynamicStyles.iconButton} onClick={() => navigate('/profile')}>
          <UserIcon />
        </button>
        <div style={styles.headerBorder} />
      </div>

      {/* Tab Navigation */}
      <div style={dynamicStyles.tabContainer}>
        <button
          style={{
            ...styles.tabButton,
            ...(activeTab === 'featured' ? dynamicStyles.activeTab : {}),
          }}
          onClick={() => setActiveTab('featured')}
        >
          <span style={{
            ...dynamicStyles.tabText,
            ...(activeTab === 'featured' ? dynamicStyles.activeTabText : {}),
          }}>Featured</span>
        </button>
        <button
          style={{
            ...styles.tabButton,
            ...(activeTab === 'near' ? dynamicStyles.activeTab : {}),
          }}
          onClick={() => setActiveTab('near')}
        >
          <span style={{
            ...dynamicStyles.tabText,
            ...(activeTab === 'near' ? dynamicStyles.activeTabText : {}),
          }}>Nearby</span>
        </button>
        <button
          style={{
            ...styles.tabButton,
            ...(activeTab === 'following' ? dynamicStyles.activeTab : {}),
          }}
          onClick={() => setActiveTab('following')}
        >
          <span style={{
            ...dynamicStyles.tabText,
            ...(activeTab === 'following' ? dynamicStyles.activeTabText : {}),
          }}>Following</span>
        </button>
      </div>

      {/* Suggestion Banner */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          margin: '0 16px 12px',
          padding: '12px 14px',
          backgroundColor: theme.accentBg,
          borderRadius: '12px',
          cursor: 'pointer',
          border: `1px solid ${theme.accent}20`,
        }}
        onClick={() => setSuggestModalOpen(true)}
      >
        <div style={{
          width: '32px',
          height: '32px',
          borderRadius: '8px',
          backgroundColor: theme.accent,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
          <PlusIcon />
        </div>
        <div style={{ flex: 1 }}>
          <span style={{ fontSize: '14px', fontWeight: '600', color: theme.text }}>
            Know a shop or event we're missing?
          </span>
          <span style={{ display: 'block', fontSize: '12px', color: theme.textSecondary }}>
            Help us grow by suggesting new locations
          </span>
        </div>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={theme.textSecondary} strokeWidth="2">
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </div>

      {/* Content */}
      <div style={styles.content}>
        {/* Featured Tab - Premium Partners */}
        {activeTab === 'featured' && (
          <div style={styles.tabContent}>
            <div style={styles.sectionHeader}>
              <span style={{ ...dynamicStyles.sectionTitle, color: '#f59e0b' }}>Premium Partners</span>
              <span style={dynamicStyles.sectionSubtitle}>Featured card shops and trading destinations</span>
            </div>
            {loading ? (
              <>
                <LocationSkeleton />
                <LocationSkeleton />
              </>
            ) : filteredLocations.filter(loc => loc.subscription_tier >= 3).length > 0 ? (
              filteredLocations
                .filter(loc => loc.subscription_tier >= 3)
                .map(location => (
                  <LocationCard
                    key={location.id}
                    location={location}
                    rating={ratings[location.id]}
                    followerData={followers[location.id]}
                    currentUserId={currentUserId}
                    onToggleFollow={handleToggleFollow}
                    tier={location.subscription_tier || 0}
                    distance={location.distance}
                    onClick={handleLocationClick}
                  />
                ))
            ) : (
              <div style={styles.emptyState}>
                <span style={dynamicStyles.emptyText}>No premium partners yet</span>
                <span style={dynamicStyles.emptySubtext}>Check Nearby for all locations</span>
                <button
                  style={{ ...dynamicStyles.findNewButton, marginTop: '16px' }}
                  onClick={() => setActiveTab('near')}
                >
                  <span style={dynamicStyles.findNewText}>Browse Nearby</span>
                </button>
              </div>
            )}
            <WaitlistCTA />
          </div>
        )}

        {/* Near You Tab */}
        {activeTab === 'near' && (
          <div style={styles.tabContent}>
            {/* Location Banner */}
            {userProfile?.latitude && userProfile?.longitude && (
              <div style={dynamicStyles.locationBanner}>
                <div style={dynamicStyles.locationBannerContent}>
                  <MapPinIcon />
                  <span style={dynamicStyles.locationBannerText}>
                    Within {searchRadius} mi of {userProfile.city || 'your location'}{userProfile.state ? `, ${userProfile.state}` : ''}
                  </span>
                </div>
                <button style={dynamicStyles.editRadiusButton} onClick={() => setRadiusModalOpen(true)}>
                  <EditIcon />
                  <span>Edit</span>
                </button>
              </div>
            )}
            <div style={styles.sectionHeader}>
              <span style={dynamicStyles.sectionTitle}>Card Shops Near You</span>
              <span style={dynamicStyles.sectionSubtitle}>Find local card shops and trading spots</span>
            </div>
            {loading ? (
              <>
                <LocationSkeleton />
                <LocationSkeleton />
                <LocationSkeleton />
              </>
            ) : filteredLocations.length > 0 ? (
              filteredLocations.map(location => (
                <LocationCard
                  key={location.id}
                  location={location}
                  rating={ratings[location.id]}
                  followerData={followers[location.id]}
                  currentUserId={currentUserId}
                  onToggleFollow={handleToggleFollow}
                  tier={location.subscription_tier || 0}
                  distance={location.distance}
                  onClick={handleLocationClick}
                />
              ))
            ) : (
              <div style={styles.emptyState}>
                <span style={dynamicStyles.emptyText}>No locations found</span>
                <span style={dynamicStyles.emptySubtext}>Try adjusting your search</span>
              </div>
            )}

            <WaitlistCTA />
          </div>
        )}

        {/* Events Tab */}
        {activeTab === 'events' && (
          <div style={styles.tabContent}>
            <div style={styles.sectionHeader}>
              <span style={dynamicStyles.sectionTitle}>Upcoming Events</span>
              <span style={dynamicStyles.sectionSubtitle}>Pokemon trading events, tournaments & more</span>
            </div>
            {eventsLoading ? (
              <>
                <LocationSkeleton />
                <LocationSkeleton />
                <LocationSkeleton />
              </>
            ) : filteredEvents.length > 0 ? (
              <>
                {filteredEvents.map(event => (
                  <EventCard
                    key={event.id}
                    event={event}
                    isSponsored={event.isSponsored}
                  />
                ))}
                {events.length >= 20 && (
                  <button style={dynamicStyles.loadMoreButton}>
                    <span style={dynamicStyles.loadMoreText}>Load More Events</span>
                  </button>
                )}
              </>
            ) : (
              <div style={styles.emptyState}>
                <span style={dynamicStyles.emptyText}>No upcoming events</span>
                <span style={dynamicStyles.emptySubtext}>Check back soon for new events</span>
              </div>
            )}
          </div>
        )}

        {/* Following Tab */}
        {activeTab === 'following' && (
          <div style={styles.tabContent}>
            <div style={styles.sectionHeader}>
              <span style={dynamicStyles.sectionTitle}>Your Followed Locations</span>
              <span style={dynamicStyles.sectionSubtitle}>Places you've saved</span>
            </div>
            {!currentUserId ? (
              <div style={styles.emptyState}>
                <span style={dynamicStyles.emptyText}>Sign in to see followed locations</span>
              </div>
            ) : followingLocations.length > 0 ? (
              followingLocations.map(location => (
                <FollowingCard
                  key={location.id}
                  location={location}
                  onUnfollow={handleUnfollow}
                />
              ))
            ) : (
              <div style={styles.emptyState}>
                <span style={dynamicStyles.emptyText}>No followed locations</span>
                <span style={dynamicStyles.emptySubtext}>Follow card shops to see them here</span>
              </div>
            )}
            {currentUserId && (
              <button style={dynamicStyles.findNewButton} onClick={() => setActiveTab('near')}>
                <span style={dynamicStyles.findNewText}>Find New Locations to Follow</span>
              </button>
            )}
            <WaitlistCTA />
          </div>
        )}
      </div>

      {/* Radius Edit Modal */}
      <RadiusEditModal
        isOpen={radiusModalOpen}
        onClose={() => setRadiusModalOpen(false)}
        radius={searchRadius}
        onRadiusChange={setSearchRadius}
      />

      {/* Search Sheet */}
      {searchOpen && (
        <>
          {/* Backdrop */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              zIndex: 999,
            }}
            onClick={closeSearch}
          />
          {/* Sheet */}
          <div style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            maxHeight: '80vh',
            backgroundColor: theme.bgSheet,
            borderTopLeftRadius: '20px',
            borderTopRightRadius: '20px',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            animation: 'slideUp 0.3s ease-out',
          }}>
            {/* Handle */}
            <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 8px' }}>
              <div style={{
                width: '36px',
                height: '4px',
                backgroundColor: theme.border,
                borderRadius: '2px',
              }} />
            </div>

            {/* Search Input */}
            <div style={{ padding: '0 16px 12px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                backgroundColor: theme.bgInput,
                borderRadius: '12px',
                padding: '12px 14px',
                border: `1px solid ${theme.border}`,
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={theme.textSecondary} strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search for a location or event..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    flex: 1,
                    backgroundColor: 'transparent',
                    border: 'none',
                    outline: 'none',
                    fontSize: '16px',
                    color: theme.text,
                  }}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={theme.textSecondary} strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="15" y1="9" x2="9" y2="15" />
                      <line x1="9" y1="9" x2="15" y2="15" />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            {/* Tabs */}
            <div style={{
              display: 'flex',
              gap: '8px',
              padding: '0 16px 12px',
            }}>
              {[
                { id: 'all', label: 'All' },
                { id: 'shops', label: 'Shops' },
                { id: 'events', label: 'Events' },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setSearchTab(tab.id)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '20px',
                    border: 'none',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    backgroundColor: searchTab === tab.id ? theme.accent : theme.bgInput,
                    color: searchTab === tab.id ? '#fff' : theme.text,
                  }}
                >
                  {tab.label}
                  {tab.id === 'shops' && searchResults.shops.length > 0 && ` (${searchResults.shops.length})`}
                  {tab.id === 'events' && searchResults.events.length > 0 && ` (${searchResults.events.length})`}
                </button>
              ))}
            </div>

            {/* Results */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '0 16px 100px',
            }}>
              {searchLoading ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: theme.textSecondary }}>
                  Searching...
                </div>
              ) : !searchQuery ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: theme.textSecondary }}>
                  Type to search for shops or events
                </div>
              ) : searchResults.shops.length === 0 && searchResults.events.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: theme.textSecondary }}>
                  No results found for "{searchQuery}"
                </div>
              ) : (
                <>
                  {/* Shops Results */}
                  {(searchTab === 'all' || searchTab === 'shops') && searchResults.shops.length > 0 && (
                    <div style={{ marginBottom: '24px' }}>
                      {searchTab === 'all' && (
                        <div style={{ fontSize: '12px', fontWeight: '600', color: theme.textSecondary, marginBottom: '12px', textTransform: 'uppercase' }}>
                          Shops ({searchResults.shops.length})
                        </div>
                      )}
                      {searchResults.shops.map(shop => (
                        <div
                          key={shop.id}
                          onClick={() => {
                            closeSearch();
                            navigate(`/location/${shop.id}`);
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '12px',
                            borderRadius: '12px',
                            marginBottom: '8px',
                            cursor: 'pointer',
                            backgroundColor: theme.bgCard,
                            border: `1px solid ${shop.subscription_tier >= 2 ? theme.warning : theme.border}`,
                            ...(shop.subscription_tier >= 2 && {
                              boxShadow: `0 0 0 1px ${theme.warningBg}`,
                            }),
                          }}
                        >
                          {/* Shop Icon with Tier Styling */}
                          <div style={{
                            width: '44px',
                            height: '44px',
                            borderRadius: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: shop.subscription_tier >= 2 ? theme.warningBg : theme.bgInput,
                            color: shop.subscription_tier >= 2 ? theme.warning : theme.textSecondary,
                          }}>
                            {shop.subscription_tier >= 2 ? (
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                              </svg>
                            ) : (
                              <MapPinIcon />
                            )}
                          </div>
                          {/* Shop Info */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span style={{
                                fontSize: '15px',
                                fontWeight: '600',
                                color: theme.text,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}>
                                {shop.store_name}
                              </span>
                              {shop.subscription_tier >= 2 && (
                                <span style={{
                                  fontSize: '10px',
                                  fontWeight: '700',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  backgroundColor: theme.warning,
                                  color: '#fff',
                                }}>
                                  PARTNER
                                </span>
                              )}
                              {shop.verified && shop.subscription_tier < 2 && (
                                <CheckCircleIcon />
                              )}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontSize: '13px', color: theme.textSecondary }}>
                                {shop.city}, {shop.state}
                              </span>
                              {shop.distance !== null && (
                                <span style={{ fontSize: '12px', color: theme.accent }}>
                                  {formatDistance(shop.distance)}
                                </span>
                              )}
                            </div>
                          </div>
                          {/* Arrow */}
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={theme.textSecondary} strokeWidth="2">
                            <polyline points="9 18 15 12 9 6" />
                          </svg>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Events Results */}
                  {(searchTab === 'all' || searchTab === 'events') && searchResults.events.length > 0 && (
                    <div>
                      {searchTab === 'all' && (
                        <div style={{ fontSize: '12px', fontWeight: '600', color: theme.textSecondary, marginBottom: '12px', textTransform: 'uppercase' }}>
                          Events ({searchResults.events.length})
                        </div>
                      )}
                      {searchResults.events.map(event => (
                        <div
                          key={event.id}
                          onClick={() => {
                            closeSearch();
                            navigate(`/location/${event.locations?.id}`);
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '12px',
                            borderRadius: '12px',
                            marginBottom: '8px',
                            cursor: 'pointer',
                            backgroundColor: theme.bgCard,
                            border: `1px solid ${theme.border}`,
                          }}
                        >
                          {/* Event Icon */}
                          <div style={{
                            width: '44px',
                            height: '44px',
                            borderRadius: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: theme.purpleBg,
                            color: theme.purple,
                          }}>
                            <CalendarIcon />
                          </div>
                          {/* Event Info */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <span style={{
                              display: 'block',
                              fontSize: '15px',
                              fontWeight: '600',
                              color: theme.text,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}>
                              {event.event_name}
                            </span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontSize: '13px', color: theme.textSecondary }}>
                                {new Date(event.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                {event.event_time && ` at ${event.event_time}`}
                              </span>
                            </div>
                            <span style={{ fontSize: '12px', color: theme.textSecondary }}>
                              {event.locations?.store_name} - {event.locations?.city}, {event.locations?.state}
                            </span>
                          </div>
                          {/* Arrow */}
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={theme.textSecondary} strokeWidth="2">
                            <polyline points="9 18 15 12 9 6" />
                          </svg>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}

      {/* Slide up animation */}
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
      `}</style>

      {/* Suggestion Modal */}
      {suggestModalOpen && (
        <>
          {/* Backdrop */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              zIndex: 999,
            }}
            onClick={closeSuggestModal}
          />
          {/* Modal */}
          <div style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            maxHeight: '90vh',
            backgroundColor: theme.bgSheet,
            borderTopLeftRadius: '20px',
            borderTopRightRadius: '20px',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            animation: 'slideUp 0.3s ease-out',
          }}>
            {/* Handle */}
            <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 8px' }}>
              <div style={{
                width: '36px',
                height: '4px',
                backgroundColor: theme.border,
                borderRadius: '2px',
              }} />
            </div>

            {/* Header */}
            <div style={{ padding: '0 16px 12px', textAlign: 'center' }}>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: theme.text }}>
                {suggestSuccess ? 'Thank You!' : 'Suggest a Location'}
              </h2>
            </div>

            {suggestSuccess ? (
              /* Success State */
              <div style={{ padding: '40px 16px', textAlign: 'center' }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  backgroundColor: theme.successBg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={theme.success} strokeWidth="2.5">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <p style={{ fontSize: '16px', color: theme.text, marginBottom: '8px' }}>
                  Your suggestion has been submitted!
                </p>
                <p style={{ fontSize: '14px', color: theme.textSecondary, marginBottom: '24px' }}>
                  We'll review it and may add it to our list soon.
                </p>
                <button
                  onClick={closeSuggestModal}
                  style={{
                    padding: '12px 32px',
                    backgroundColor: theme.accent,
                    color: '#fff',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  Done
                </button>
              </div>
            ) : (
              <>
                {/* Tabs */}
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  padding: '0 16px 16px',
                  justifyContent: 'center',
                }}>
                  <button
                    onClick={() => setSuggestTab('store')}
                    style={{
                      flex: 1,
                      padding: '10px 16px',
                      borderRadius: '10px',
                      border: 'none',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      backgroundColor: suggestTab === 'store' ? theme.accent : theme.bgInput,
                      color: suggestTab === 'store' ? '#fff' : theme.text,
                    }}
                  >
                    Suggest Store
                  </button>
                  <button
                    onClick={() => setSuggestTab('event')}
                    style={{
                      flex: 1,
                      padding: '10px 16px',
                      borderRadius: '10px',
                      border: 'none',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      backgroundColor: suggestTab === 'event' ? theme.accent : theme.bgInput,
                      color: suggestTab === 'event' ? '#fff' : theme.text,
                    }}
                  >
                    Suggest Event
                  </button>
                </div>

                {/* Form */}
                <div style={{
                  flex: 1,
                  overflowY: 'auto',
                  padding: '0 16px 100px',
                }}>
                  {suggestTab === 'store' ? (
                    /* Store Form */
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      {/* Store Name */}
                      <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: theme.text, marginBottom: '6px' }}>
                          Store Name <span style={{ color: theme.error }}>*</span>
                        </label>
                        <input
                          type="text"
                          placeholder="e.g. Epic Card Games"
                          value={storeForm.storeName}
                          onChange={(e) => setStoreForm(prev => ({ ...prev, storeName: e.target.value }))}
                          style={{
                            width: '100%',
                            padding: '12px',
                            borderRadius: '10px',
                            border: `1px solid ${theme.border}`,
                            backgroundColor: theme.bgInput,
                            color: theme.text,
                            fontSize: '15px',
                            boxSizing: 'border-box',
                          }}
                        />
                      </div>

                      {/* Address */}
                      <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: theme.text, marginBottom: '6px' }}>
                          Address
                        </label>
                        <input
                          type="text"
                          placeholder="123 Main St"
                          value={storeForm.address}
                          onChange={(e) => setStoreForm(prev => ({ ...prev, address: e.target.value }))}
                          style={{
                            width: '100%',
                            padding: '12px',
                            borderRadius: '10px',
                            border: `1px solid ${theme.border}`,
                            backgroundColor: theme.bgInput,
                            color: theme.text,
                            fontSize: '15px',
                            boxSizing: 'border-box',
                          }}
                        />
                      </div>

                      {/* City, State, Zip */}
                      <div style={{ display: 'flex', gap: '12px' }}>
                        <div style={{ flex: 2 }}>
                          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: theme.text, marginBottom: '6px' }}>
                            City <span style={{ color: theme.error }}>*</span>
                          </label>
                          <input
                            type="text"
                            placeholder="Austin"
                            value={storeForm.city}
                            onChange={(e) => setStoreForm(prev => ({ ...prev, city: e.target.value }))}
                            style={{
                              width: '100%',
                              padding: '12px',
                              borderRadius: '10px',
                              border: `1px solid ${theme.border}`,
                              backgroundColor: theme.bgInput,
                              color: theme.text,
                              fontSize: '15px',
                              boxSizing: 'border-box',
                            }}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: theme.text, marginBottom: '6px' }}>
                            State <span style={{ color: theme.error }}>*</span>
                          </label>
                          <input
                            type="text"
                            placeholder="TX"
                            maxLength={2}
                            value={storeForm.state}
                            onChange={(e) => setStoreForm(prev => ({ ...prev, state: e.target.value.toUpperCase() }))}
                            style={{
                              width: '100%',
                              padding: '12px',
                              borderRadius: '10px',
                              border: `1px solid ${theme.border}`,
                              backgroundColor: theme.bgInput,
                              color: theme.text,
                              fontSize: '15px',
                              boxSizing: 'border-box',
                            }}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: theme.text, marginBottom: '6px' }}>
                            Zip
                          </label>
                          <input
                            type="text"
                            placeholder="78701"
                            maxLength={5}
                            value={storeForm.zipCode}
                            onChange={(e) => setStoreForm(prev => ({ ...prev, zipCode: e.target.value }))}
                            style={{
                              width: '100%',
                              padding: '12px',
                              borderRadius: '10px',
                              border: `1px solid ${theme.border}`,
                              backgroundColor: theme.bgInput,
                              color: theme.text,
                              fontSize: '15px',
                              boxSizing: 'border-box',
                            }}
                          />
                        </div>
                      </div>

                      {/* Phone & Website */}
                      <div style={{ display: 'flex', gap: '12px' }}>
                        <div style={{ flex: 1 }}>
                          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: theme.text, marginBottom: '6px' }}>
                            Store Phone
                          </label>
                          <input
                            type="tel"
                            placeholder="(555) 123-4567"
                            value={storeForm.phone}
                            onChange={(e) => setStoreForm(prev => ({ ...prev, phone: e.target.value }))}
                            style={{
                              width: '100%',
                              padding: '12px',
                              borderRadius: '10px',
                              border: `1px solid ${theme.border}`,
                              backgroundColor: theme.bgInput,
                              color: theme.text,
                              fontSize: '15px',
                              boxSizing: 'border-box',
                            }}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: theme.text, marginBottom: '6px' }}>
                            Website
                          </label>
                          <input
                            type="url"
                            placeholder="www.epiccardgames.com"
                            value={storeForm.website}
                            onChange={(e) => setStoreForm(prev => ({ ...prev, website: e.target.value }))}
                            style={{
                              width: '100%',
                              padding: '12px',
                              borderRadius: '10px',
                              border: `1px solid ${theme.border}`,
                              backgroundColor: theme.bgInput,
                              color: theme.text,
                              fontSize: '15px',
                              boxSizing: 'border-box',
                            }}
                          />
                        </div>
                      </div>

                      {/* Are you the owner? */}
                      <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: theme.text, marginBottom: '8px' }}>
                          Are you the owner of this store? <span style={{ color: theme.error }}>*</span>
                        </label>
                        <div style={{ display: 'flex', gap: '12px' }}>
                          <button
                            type="button"
                            onClick={() => setStoreForm(prev => ({ ...prev, isOwner: true, relationship: '' }))}
                            style={{
                              flex: 1,
                              padding: '14px',
                              borderRadius: '10px',
                              border: `2px solid ${storeForm.isOwner === true ? theme.accent : theme.border}`,
                              backgroundColor: storeForm.isOwner === true ? theme.accentBg : theme.bgInput,
                              color: storeForm.isOwner === true ? theme.accent : theme.text,
                              fontSize: '15px',
                              fontWeight: '600',
                              cursor: 'pointer',
                            }}
                          >
                            Yes, I'm the owner
                          </button>
                          <button
                            type="button"
                            onClick={() => setStoreForm(prev => ({ ...prev, isOwner: false }))}
                            style={{
                              flex: 1,
                              padding: '14px',
                              borderRadius: '10px',
                              border: `2px solid ${storeForm.isOwner === false ? theme.accent : theme.border}`,
                              backgroundColor: storeForm.isOwner === false ? theme.accentBg : theme.bgInput,
                              color: storeForm.isOwner === false ? theme.accent : theme.text,
                              fontSize: '15px',
                              fontWeight: '600',
                              cursor: 'pointer',
                            }}
                          >
                            No
                          </button>
                        </div>

                        {/* Relationship field - shows when NOT owner */}
                        {storeForm.isOwner === false && (
                          <div style={{ marginTop: '12px' }}>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: theme.text, marginBottom: '6px' }}>
                              How do you know this store?
                            </label>
                            <select
                              value={storeForm.relationship}
                              onChange={(e) => setStoreForm(prev => ({ ...prev, relationship: e.target.value }))}
                              style={{
                                width: '100%',
                                padding: '12px',
                                borderRadius: '10px',
                                border: `1px solid ${theme.border}`,
                                backgroundColor: theme.bgInput,
                                color: theme.text,
                                fontSize: '15px',
                                boxSizing: 'border-box',
                                cursor: 'pointer',
                              }}
                            >
                              <option value="">Select...</option>
                              <option value="customer">I'm a customer</option>
                              <option value="employee">I work there</option>
                              <option value="friend">I know the owner</option>
                              <option value="visited">I've visited before</option>
                              <option value="heard">I've heard about it</option>
                              <option value="other">Other</option>
                            </select>

                            {/* Is this store near you? */}
                            <div style={{ marginTop: '12px' }}>
                              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: theme.text, marginBottom: '8px' }}>
                                How did you find this store?
                              </label>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                <button
                                  type="button"
                                  onClick={() => setStoreForm(prev => ({ ...prev, isNearby: 'local' }))}
                                  style={{
                                    padding: '8px 14px',
                                    borderRadius: '8px',
                                    border: `2px solid ${storeForm.isNearby === 'local' ? theme.accent : theme.border}`,
                                    backgroundColor: storeForm.isNearby === 'local' ? theme.accentBg : theme.bgInput,
                                    color: storeForm.isNearby === 'local' ? theme.accent : theme.text,
                                    fontSize: '13px',
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                  }}
                                >
                                  It's local to me
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setStoreForm(prev => ({ ...prev, isNearby: 'traveling' }))}
                                  style={{
                                    padding: '8px 14px',
                                    borderRadius: '8px',
                                    border: `2px solid ${storeForm.isNearby === 'traveling' ? theme.accent : theme.border}`,
                                    backgroundColor: storeForm.isNearby === 'traveling' ? theme.accentBg : theme.bgInput,
                                    color: storeForm.isNearby === 'traveling' ? theme.accent : theme.text,
                                    fontSize: '13px',
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                  }}
                                >
                                  Found while traveling
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setStoreForm(prev => ({ ...prev, isNearby: 'online' }))}
                                  style={{
                                    padding: '8px 14px',
                                    borderRadius: '8px',
                                    border: `2px solid ${storeForm.isNearby === 'online' ? theme.accent : theme.border}`,
                                    backgroundColor: storeForm.isNearby === 'online' ? theme.accentBg : theme.bgInput,
                                    color: storeForm.isNearby === 'online' ? theme.accent : theme.text,
                                    fontSize: '13px',
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                  }}
                                >
                                  Looked it up online
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Notes */}
                      <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: theme.text, marginBottom: '6px' }}>
                          Additional Notes
                        </label>
                        <textarea
                          placeholder="Why should we add this store? Any other details..."
                          value={storeForm.notes}
                          onChange={(e) => setStoreForm(prev => ({ ...prev, notes: e.target.value }))}
                          rows={3}
                          style={{
                            width: '100%',
                            padding: '12px',
                            borderRadius: '10px',
                            border: `1px solid ${theme.border}`,
                            backgroundColor: theme.bgInput,
                            color: theme.text,
                            fontSize: '15px',
                            boxSizing: 'border-box',
                            resize: 'none',
                          }}
                        />
                      </div>

                      {/* Submit Button */}
                      <button
                        onClick={submitStoreSuggestion}
                        disabled={!storeForm.storeName || !storeForm.city || !storeForm.state || storeForm.isOwner === null || suggestSubmitting}
                        style={{
                          width: '100%',
                          padding: '14px',
                          backgroundColor: (!storeForm.storeName || !storeForm.city || !storeForm.state || storeForm.isOwner === null) ? theme.bgHover : theme.accent,
                          color: (!storeForm.storeName || !storeForm.city || !storeForm.state || storeForm.isOwner === null) ? theme.textSecondary : '#fff',
                          border: 'none',
                          borderRadius: '12px',
                          fontSize: '16px',
                          fontWeight: '600',
                          cursor: (!storeForm.storeName || !storeForm.city || !storeForm.state || storeForm.isOwner === null) ? 'not-allowed' : 'pointer',
                          marginTop: '8px',
                        }}
                      >
                        {suggestSubmitting ? 'Submitting...' : 'Submit Store Suggestion'}
                      </button>
                    </div>
                  ) : (
                    /* Event Form */
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      {/* 1. Are you the organizer? */}
                      <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: theme.text, marginBottom: '8px' }}>
                          Are you the organizer of this event? <span style={{ color: theme.error }}>*</span>
                        </label>
                        <div style={{ display: 'flex', gap: '10px' }}>
                          <button
                            type="button"
                            onClick={() => setEventForm(prev => ({ ...prev, isOrganizer: true, sourceType: '' }))}
                            style={{
                              flex: 1,
                              padding: '12px',
                              borderRadius: '8px',
                              border: `2px solid ${eventForm.isOrganizer === true ? theme.accent : theme.border}`,
                              backgroundColor: eventForm.isOrganizer === true ? theme.accentBg : theme.bgInput,
                              color: eventForm.isOrganizer === true ? theme.accent : theme.text,
                              fontSize: '14px',
                              fontWeight: '600',
                              cursor: 'pointer',
                            }}
                          >
                            Yes, I'm the organizer
                          </button>
                          <button
                            type="button"
                            onClick={() => setEventForm(prev => ({ ...prev, isOrganizer: false }))}
                            style={{
                              flex: 1,
                              padding: '12px',
                              borderRadius: '8px',
                              border: `2px solid ${eventForm.isOrganizer === false ? theme.accent : theme.border}`,
                              backgroundColor: eventForm.isOrganizer === false ? theme.accentBg : theme.bgInput,
                              color: eventForm.isOrganizer === false ? theme.accent : theme.text,
                              fontSize: '14px',
                              fontWeight: '600',
                              cursor: 'pointer',
                            }}
                          >
                            No
                          </button>
                        </div>

                        {/* If not organizer - show source text field */}
                        {eventForm.isOrganizer === false && (
                          <div style={{ marginTop: '12px' }}>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: theme.textSecondary, marginBottom: '6px' }}>
                              How did you hear about it?
                            </label>
                            <input
                              type="text"
                              placeholder="Facebook, Instagram, a friend, etc."
                              value={eventForm.sourceType}
                              onChange={(e) => setEventForm(prev => ({ ...prev, sourceType: e.target.value }))}
                              style={{
                                width: '100%',
                                padding: '12px',
                                borderRadius: '10px',
                                border: `1px solid ${theme.border}`,
                                backgroundColor: theme.bgInput,
                                color: theme.text,
                                fontSize: '15px',
                                boxSizing: 'border-box',
                              }}
                            />
                          </div>
                        )}
                      </div>

                      {/* 2. Is this event officially scheduled? */}
                      <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: theme.text, marginBottom: '8px' }}>
                          Is this event officially scheduled? <span style={{ color: theme.error }}>*</span>
                        </label>
                        <div style={{ display: 'flex', gap: '10px' }}>
                          <button
                            type="button"
                            onClick={() => setEventForm(prev => ({ ...prev, isConfirmed: true }))}
                            style={{
                              flex: 1,
                              padding: '12px',
                              borderRadius: '8px',
                              border: `2px solid ${eventForm.isConfirmed === true ? theme.accent : theme.border}`,
                              backgroundColor: eventForm.isConfirmed === true ? theme.accentBg : theme.bgInput,
                              color: eventForm.isConfirmed === true ? theme.accent : theme.text,
                              fontSize: '14px',
                              fontWeight: '600',
                              cursor: 'pointer',
                            }}
                          >
                            Yes, it's confirmed
                          </button>
                          <button
                            type="button"
                            onClick={() => setEventForm(prev => ({ ...prev, isConfirmed: false }))}
                            style={{
                              flex: 1,
                              padding: '12px',
                              borderRadius: '8px',
                              border: `2px solid ${eventForm.isConfirmed === false ? theme.accent : theme.border}`,
                              backgroundColor: eventForm.isConfirmed === false ? theme.accentBg : theme.bgInput,
                              color: eventForm.isConfirmed === false ? theme.accent : theme.text,
                              fontSize: '14px',
                              fontWeight: '600',
                              cursor: 'pointer',
                            }}
                          >
                            Not sure
                          </button>
                        </div>
                      </div>

                      {/* 3. Event Name */}
                      <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: theme.text, marginBottom: '6px' }}>
                          Event Name <span style={{ color: theme.error }}>*</span>
                        </label>
                        <input
                          type="text"
                          placeholder="e.g. Pokemon Card Show"
                          value={eventForm.eventName}
                          onChange={(e) => setEventForm(prev => ({ ...prev, eventName: e.target.value }))}
                          style={{
                            width: '100%',
                            padding: '12px',
                            borderRadius: '10px',
                            border: `1px solid ${theme.border}`,
                            backgroundColor: theme.bgInput,
                            color: theme.text,
                            fontSize: '15px',
                            boxSizing: 'border-box',
                          }}
                        />
                      </div>

                      {/* Link to event page - always shown */}
                      <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: theme.text, marginBottom: '6px' }}>
                          Link to event page <span style={{ color: theme.error }}>*</span>
                        </label>
                        <input
                          type="url"
                          placeholder="https://facebook.com/events/..."
                          value={eventForm.sourceLink}
                          onChange={(e) => setEventForm(prev => ({ ...prev, sourceLink: e.target.value }))}
                          style={{
                            width: '100%',
                            padding: '12px',
                            borderRadius: '10px',
                            border: `1px solid ${theme.border}`,
                            backgroundColor: theme.bgInput,
                            color: theme.text,
                            fontSize: '15px',
                            boxSizing: 'border-box',
                          }}
                        />
                        <p style={{ fontSize: '12px', color: theme.textSecondary, margin: '4px 0 0 0' }}>
                          Facebook, Instagram, website, etc.
                        </p>
                      </div>

                      {/* Event frequency */}
                      <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: theme.text, marginBottom: '8px' }}>
                          How often is this event held?
                        </label>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          <button
                            type="button"
                            onClick={() => setEventForm(prev => ({ ...prev, frequency: 'weekly' }))}
                            style={{
                              padding: '8px 14px',
                              borderRadius: '8px',
                              border: `2px solid ${eventForm.frequency === 'weekly' ? theme.accent : theme.border}`,
                              backgroundColor: eventForm.frequency === 'weekly' ? theme.accentBg : theme.bgInput,
                              color: eventForm.frequency === 'weekly' ? theme.accent : theme.text,
                              fontSize: '13px',
                              fontWeight: '500',
                              cursor: 'pointer',
                            }}
                          >
                            Weekly
                          </button>
                          <button
                            type="button"
                            onClick={() => setEventForm(prev => ({ ...prev, frequency: 'monthly' }))}
                            style={{
                              padding: '8px 14px',
                              borderRadius: '8px',
                              border: `2px solid ${eventForm.frequency === 'monthly' ? theme.accent : theme.border}`,
                              backgroundColor: eventForm.frequency === 'monthly' ? theme.accentBg : theme.bgInput,
                              color: eventForm.frequency === 'monthly' ? theme.accent : theme.text,
                              fontSize: '13px',
                              fontWeight: '500',
                              cursor: 'pointer',
                            }}
                          >
                            Monthly
                          </button>
                          <button
                            type="button"
                            onClick={() => setEventForm(prev => ({ ...prev, frequency: 'regular' }))}
                            style={{
                              padding: '8px 14px',
                              borderRadius: '8px',
                              border: `2px solid ${eventForm.frequency === 'regular' ? theme.accent : theme.border}`,
                              backgroundColor: eventForm.frequency === 'regular' ? theme.accentBg : theme.bgInput,
                              color: eventForm.frequency === 'regular' ? theme.accent : theme.text,
                              fontSize: '13px',
                              fontWeight: '500',
                              cursor: 'pointer',
                            }}
                          >
                            Regular schedule
                          </button>
                          <button
                            type="button"
                            onClick={() => setEventForm(prev => ({ ...prev, frequency: 'irregular' }))}
                            style={{
                              padding: '8px 14px',
                              borderRadius: '8px',
                              border: `2px solid ${eventForm.frequency === 'irregular' ? theme.accent : theme.border}`,
                              backgroundColor: eventForm.frequency === 'irregular' ? theme.accentBg : theme.bgInput,
                              color: eventForm.frequency === 'irregular' ? theme.accent : theme.text,
                              fontSize: '13px',
                              fontWeight: '500',
                              cursor: 'pointer',
                            }}
                          >
                            One-time / Irregular
                          </button>
                        </div>
                      </div>

                      {/* Venue & Address */}
                      <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: theme.text, marginBottom: '6px' }}>
                          Venue / Location Name
                        </label>
                        <input
                          type="text"
                          placeholder="e.g. Convention Center"
                          value={eventForm.venueName}
                          onChange={(e) => setEventForm(prev => ({ ...prev, venueName: e.target.value }))}
                          style={{
                            width: '100%',
                            padding: '12px',
                            borderRadius: '10px',
                            border: `1px solid ${theme.border}`,
                            backgroundColor: theme.bgInput,
                            color: theme.text,
                            fontSize: '15px',
                            boxSizing: 'border-box',
                          }}
                        />
                      </div>

                      <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: theme.text, marginBottom: '6px' }}>
                          Address
                        </label>
                        <input
                          type="text"
                          placeholder="123 Main St"
                          value={eventForm.address}
                          onChange={(e) => setEventForm(prev => ({ ...prev, address: e.target.value }))}
                          style={{
                            width: '100%',
                            padding: '12px',
                            borderRadius: '10px',
                            border: `1px solid ${theme.border}`,
                            backgroundColor: theme.bgInput,
                            color: theme.text,
                            fontSize: '15px',
                            boxSizing: 'border-box',
                          }}
                        />
                      </div>

                      {/* City & State */}
                      <div style={{ display: 'flex', gap: '12px' }}>
                        <div style={{ flex: 2 }}>
                          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: theme.text, marginBottom: '6px' }}>
                            City <span style={{ color: theme.error }}>*</span>
                          </label>
                          <input
                            type="text"
                            placeholder="Austin"
                            value={eventForm.city}
                            onChange={(e) => setEventForm(prev => ({ ...prev, city: e.target.value }))}
                            style={{
                              width: '100%',
                              padding: '12px',
                              borderRadius: '10px',
                              border: `1px solid ${theme.border}`,
                              backgroundColor: theme.bgInput,
                              color: theme.text,
                              fontSize: '15px',
                              boxSizing: 'border-box',
                            }}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: theme.text, marginBottom: '6px' }}>
                            State <span style={{ color: theme.error }}>*</span>
                          </label>
                          <input
                            type="text"
                            placeholder="TX"
                            maxLength={2}
                            value={eventForm.state}
                            onChange={(e) => setEventForm(prev => ({ ...prev, state: e.target.value.toUpperCase() }))}
                            style={{
                              width: '100%',
                              padding: '12px',
                              borderRadius: '10px',
                              border: `1px solid ${theme.border}`,
                              backgroundColor: theme.bgInput,
                              color: theme.text,
                              fontSize: '15px',
                              boxSizing: 'border-box',
                            }}
                          />
                        </div>
                      </div>

                      {/* Date & Time */}
                      <div style={{ display: 'flex', gap: '12px' }}>
                        <div style={{ flex: 1 }}>
                          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: theme.text, marginBottom: '6px' }}>
                            Event Date
                          </label>
                          <input
                            type="date"
                            value={eventForm.eventDate}
                            onChange={(e) => setEventForm(prev => ({ ...prev, eventDate: e.target.value }))}
                            style={{
                              width: '100%',
                              padding: '12px',
                              borderRadius: '10px',
                              border: `1px solid ${theme.border}`,
                              backgroundColor: theme.bgInput,
                              color: theme.text,
                              fontSize: '15px',
                              boxSizing: 'border-box',
                            }}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: theme.text, marginBottom: '6px' }}>
                            Event Time
                          </label>
                          <input
                            type="text"
                            placeholder="10am - 4pm"
                            value={eventForm.eventTime}
                            onChange={(e) => setEventForm(prev => ({ ...prev, eventTime: e.target.value }))}
                            style={{
                              width: '100%',
                              padding: '12px',
                              borderRadius: '10px',
                              border: `1px solid ${theme.border}`,
                              backgroundColor: theme.bgInput,
                              color: theme.text,
                              fontSize: '15px',
                              boxSizing: 'border-box',
                            }}
                          />
                        </div>
                      </div>
                      <p style={{ fontSize: '12px', color: theme.textSecondary, margin: '-8px 0 0 0' }}>
                        If multiple days, just put the first day and we'll confirm the rest.
                      </p>

                      {/* Description */}
                      <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: theme.text, marginBottom: '6px' }}>
                          Description
                        </label>
                        <textarea
                          placeholder="Tell us about the event - trading, vendors, card show, etc. More details = better chance of approval!"
                          value={eventForm.description}
                          onChange={(e) => setEventForm(prev => ({ ...prev, description: e.target.value }))}
                          rows={3}
                          style={{
                            width: '100%',
                            padding: '12px',
                            borderRadius: '10px',
                            border: `1px solid ${theme.border}`,
                            backgroundColor: theme.bgInput,
                            color: theme.text,
                            fontSize: '15px',
                            boxSizing: 'border-box',
                            resize: 'none',
                          }}
                        />
                      </div>

                      {/* Expected Vendors */}
                      <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: theme.text, marginBottom: '6px' }}>
                          Approx. Number of Vendors (if any)
                        </label>
                        <input
                          type="number"
                          placeholder="e.g. 20"
                          value={eventForm.expectedVendors}
                          onChange={(e) => setEventForm(prev => ({ ...prev, expectedVendors: e.target.value }))}
                          style={{
                            width: '100%',
                            padding: '12px',
                            borderRadius: '10px',
                            border: `1px solid ${theme.border}`,
                            backgroundColor: theme.bgInput,
                            color: theme.text,
                            fontSize: '15px',
                            boxSizing: 'border-box',
                          }}
                        />
                      </div>

                      {/* Notes */}
                      <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: theme.text, marginBottom: '6px' }}>
                          Additional Notes
                        </label>
                        <textarea
                          placeholder="Anything else we should know about this event..."
                          value={eventForm.notes}
                          onChange={(e) => setEventForm(prev => ({ ...prev, notes: e.target.value }))}
                          rows={3}
                          style={{
                            width: '100%',
                            padding: '12px',
                            borderRadius: '10px',
                            border: `1px solid ${theme.border}`,
                            backgroundColor: theme.bgInput,
                            color: theme.text,
                            fontSize: '15px',
                            boxSizing: 'border-box',
                            resize: 'none',
                          }}
                        />
                      </div>

                      {/* Submit Button */}
                      <button
                        onClick={submitEventSuggestion}
                        disabled={!eventForm.eventName || !eventForm.city || !eventForm.state || !eventForm.sourceLink || suggestSubmitting}
                        style={{
                          width: '100%',
                          padding: '14px',
                          backgroundColor: (!eventForm.eventName || !eventForm.city || !eventForm.state || !eventForm.sourceLink) ? theme.bgHover : theme.accent,
                          color: (!eventForm.eventName || !eventForm.city || !eventForm.state || !eventForm.sourceLink) ? theme.textSecondary : '#fff',
                          border: 'none',
                          borderRadius: '12px',
                          fontSize: '16px',
                          fontWeight: '600',
                          cursor: (!eventForm.eventName || !eventForm.city || !eventForm.state || !eventForm.sourceLink) ? 'not-allowed' : 'pointer',
                          marginTop: '8px',
                        }}
                      >
                        {suggestSubmitting ? 'Submitting...' : 'Submit Event Suggestion'}
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const styles = {
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  // Header
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    flexShrink: 0,
    position: 'relative',
  },
  headerTitle: {
    fontSize: '20px',
    fontWeight: '700',
    margin: 0,
    flex: 1,
  },
  iconButton: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    border: '1px solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  headerBorder: {
    position: 'absolute',
    bottom: 0,
    left: '40px',
    right: '40px',
    height: '2px',
    backgroundColor: 'rgba(255, 255, 255, 0.35)',
    borderRadius: '1px',
  },
  // Tabs
  tabContainer: {
    display: 'flex',
    gap: '4px',
    borderRadius: '10px',
    padding: '4px',
    margin: '12px 16px 12px',
  },
  tabButton: {
    flex: 1,
    padding: '10px 8px',
    borderRadius: '8px',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  activeTab: {
  },
  tabText: {
    fontSize: '14px',
    fontWeight: '500',
  },
  activeTabText: {
  },
  // Content
  content: {
    flex: 1,
    overflow: 'auto',
    paddingBottom: '100px',
  },
  tabContent: {
    padding: '0 16px',
  },
  // Location Banner
  locationBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: '10px',
    padding: '10px 14px',
    marginBottom: '16px',
    border: '1px solid',
  },
  locationBannerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  locationBannerText: {
    fontSize: '13px',
  },
  editRadiusButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '6px 10px',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  sectionHeader: {
    marginBottom: '16px',
  },
  sectionTitle: {
    display: 'block',
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '4px',
  },
  sectionSubtitle: {
    display: 'block',
    fontSize: '14px',
  },
  // Location Card
  locationCard: {
    borderRadius: '12px',
    marginBottom: '12px',
  },
  cardImageContainer: {
    position: 'relative',
    height: 120,
    marginBottom: 0,
  },
  cardImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  premiumBanner: {
    position: 'absolute',
    top: 8,
    right: 8,
    backgroundColor: 'rgba(245, 158, 11, 0.9)',
    padding: '4px 10px',
    borderRadius: 4,
  },
  premiumBannerText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  locationHeader: {
    padding: '16px',
    paddingBottom: '0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '12px',
  },
  locationInfo: {
    flex: 1,
    marginRight: '12px',
  },
  nameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
    marginBottom: '6px',
  },
  locationName: {
    fontSize: '16px',
    fontWeight: '600',
  },
  verifiedBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    backgroundColor: 'rgba(16, 185, 129, 0.1)',
    padding: '3px 8px',
    borderRadius: '6px',
    border: '1px solid rgba(16, 185, 129, 0.3)',
    color: '#10b981',
  },
  verifiedText: {
    fontSize: '11px',
    fontWeight: '500',
  },
  addressRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginBottom: '8px',
    flexWrap: 'nowrap',
  },
  locationAddress: {
    fontSize: '14px',
  },
  distanceText: {
    fontWeight: '500',
  },
  distanceBadge: {
    marginLeft: 'auto',
    padding: '2px 8px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '600',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  tradingBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: '8px',
  },
  tradingBadgeText: {
    fontSize: '12px',
    fontWeight: '600',
  },
  followButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '6px 12px',
    borderRadius: '16px',
    backgroundColor: 'transparent',
    border: '1px solid',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  followingButton: {
  },
  followText: {
    fontSize: '12px',
    fontWeight: '500',
  },
  followingText: {
    fontSize: '12px',
    fontWeight: '500',
  },
  statsRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '0 16px 16px 16px',
  },
  statItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flex: 1,
    color: '#fbbf24',
  },
  statText: {
    fontSize: '14px',
    fontWeight: '500',
  },
  statDivider: {
    width: '1px',
    height: '16px',
    margin: '0 12px',
  },
  // Event Card
  eventCard: {
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '12px',
    border: '1px solid',
  },
  sponsoredEventCard: {
  },
  sponsoredBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginBottom: '10px',
  },
  sponsoredIcon: {
    fontSize: '12px',
  },
  sponsoredText: {
    fontSize: '11px',
    fontWeight: '600',
    letterSpacing: '0.5px',
  },
  eventContent: {
    display: 'flex',
    gap: '12px',
  },
  eventImageContainer: {
    width: '48px',
    height: '48px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  eventImageText: {
    fontSize: '12px',
    fontWeight: '700',
  },
  eventDetails: {
    flex: 1,
  },
  eventTitle: {
    display: 'block',
    fontSize: '15px',
    fontWeight: '600',
    marginBottom: '4px',
  },
  eventLocation: {
    display: 'block',
    fontSize: '14px',
    marginBottom: '8px',
  },
  eventMetaRow: {
    display: 'flex',
    gap: '16px',
    marginBottom: '10px',
  },
  eventMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  eventMetaText: {
    fontSize: '12px',
  },
  eventFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  eventTypeBadge: {
    padding: '4px 10px',
    borderRadius: '12px',
    border: '1px solid',
  },
  externalVenueBadge: {
    backgroundColor: 'rgba(168, 85, 247, 0.1)',
    borderColor: '#7c3aed',
  },
  storeEventBadge: {
    backgroundColor: 'rgba(34, 197, 94, 0.1)',
    borderColor: '#16a34a',
  },
  eventTypeText: {
    fontSize: '12px',
    fontWeight: '500',
  },
  externalVenueText: {
  },
  storeEventText: {
  },
  entryFee: {
    fontSize: '14px',
    fontWeight: '600',
  },
  loadMoreButton: {
    width: '100%',
    padding: '12px',
    backgroundColor: 'transparent',
    border: '1px solid',
    borderRadius: '12px',
    cursor: 'pointer',
    marginTop: '16px',
  },
  loadMoreText: {
    fontSize: '14px',
    fontWeight: '500',
  },
  // Following Card
  followingCard: {
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '12px',
    border: '1px solid',
  },
  followingHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  followingImageContainer: {
    width: '48px',
    height: '48px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  followingImageText: {
    fontSize: '14px',
    fontWeight: '700',
  },
  followingInfo: {
    flex: 1,
  },
  followingName: {
    display: 'block',
    fontSize: '16px',
    fontWeight: '600',
    marginBottom: '4px',
  },
  followingMetaRow: {
    display: 'flex',
    alignItems: 'center',
  },
  followingMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  followingMetaText: {
    fontSize: '14px',
  },
  unfollowButton: {
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  followingRatings: {
    display: 'flex',
    justifyContent: 'space-around',
    marginTop: '12px',
    paddingTop: '12px',
    borderTop: '1px solid',
  },
  ratingItem: {
    textAlign: 'center',
  },
  ratingLabel: {
    display: 'block',
    fontSize: '12px',
    marginBottom: '4px',
  },
  ratingValue: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    color: '#fbbf24',
    fontSize: '14px',
    fontWeight: '500',
  },
  findNewButton: {
    width: '100%',
    padding: '14px',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    marginTop: '16px',
  },
  findNewText: {
    fontSize: '14px',
    fontWeight: '600',
  },
  // Empty State
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 16px',
    gap: '8px',
  },
  emptyText: {
    fontSize: '16px',
    fontWeight: '500',
  },
  emptySubtext: {
    fontSize: '14px',
  },
  // Skeleton
  skeletonCard: {
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '12px',
    border: '1px solid',
  },
  skeletonHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '12px',
  },
  skeletonTitle: {
    width: '150px',
    height: '20px',
    borderRadius: '4px',
    animation: 'pulse 1.5s ease-in-out infinite',
  },
  skeletonButton: {
    width: '70px',
    height: '28px',
    borderRadius: '14px',
    animation: 'pulse 1.5s ease-in-out infinite',
  },
  skeletonAddress: {
    width: '200px',
    height: '16px',
    borderRadius: '4px',
    marginBottom: '12px',
    animation: 'pulse 1.5s ease-in-out infinite',
  },
  skeletonStats: {
    width: '100%',
    height: '20px',
    borderRadius: '4px',
    animation: 'pulse 1.5s ease-in-out infinite',
  },
};

export default Locations;
